import React from "react";

/** North-Indian style Rashi chart (D1/D9).
 *  Renders a square with two diagonals and two mid-line diamonds — the classic North
 *  Indian layout. House 1 is the top diamond; houses continue anti-clockwise.
 *  props:
 *    houses  – array of 12 { house, sign, sign_index, planets[] }
 *    title   – "D1 · Rashi" etc
 *    highlight – 1..12 to indicate ascendant (draws an "L" badge in the corresponding cell)
 */
export default function NorthIndianChart({ houses = [], title = "D1 · Rashi", highlight = 1, size = 320 }) {
  if (!houses || houses.length !== 12) return null;
  const s = size;
  const m = s / 2;

  // Approx cell centres — tuned to look natural on the North-Indian layout.
  const centres = {
    1:  [m,        s * 0.28],
    2:  [s * 0.30, s * 0.13],
    3:  [s * 0.14, s * 0.28],
    4:  [s * 0.30, m         ],
    5:  [s * 0.14, s * 0.72],
    6:  [s * 0.30, s * 0.87],
    7:  [m,        s * 0.72],
    8:  [s * 0.70, s * 0.87],
    9:  [s * 0.86, s * 0.72],
    10: [s * 0.70, m         ],
    11: [s * 0.86, s * 0.28],
    12: [s * 0.70, s * 0.13],
  };

  return (
    <div className="inline-block w-full max-w-full" data-testid={`chart-${title.split(" ")[0].toLowerCase()}`}>
      <div className="text-xs uppercase tracking-widest text-foreground/60 mb-2 font-semibold">{title}</div>
      <svg viewBox={`0 0 ${s} ${s}`} className="w-full h-auto" role="img" aria-label={title}>
        <rect x="0.5" y="0.5" width={s - 1} height={s - 1} fill="var(--yah-bg,transparent)" stroke="#0B1F3A" strokeWidth="1.5" />
        <line x1="0" y1="0" x2={s} y2={s} stroke="#0B1F3A" strokeWidth="1" />
        <line x1={s} y1="0" x2="0" y2={s} stroke="#0B1F3A" strokeWidth="1" />
        <line x1="0" y1={m} x2={m} y2="0" stroke="#0B1F3A" strokeWidth="1" />
        <line x1={m} y1="0" x2={s} y2={m} stroke="#0B1F3A" strokeWidth="1" />
        <line x1={s} y1={m} x2={m} y2={s} stroke="#0B1F3A" strokeWidth="1" />
        <line x1={m} y1={s} x2="0" y2={m} stroke="#0B1F3A" strokeWidth="1" />

        {houses.map((h) => {
          const [cx, cy] = centres[h.house];
          const isLagna = h.house === 1;
          return (
            <g key={h.house}>
              {/* sign number, top-left of the cell (traditional). Position offset per cell to stay inside */}
              <text x={cx - s * 0.06} y={cy - s * 0.03} fontSize={s * 0.032} fontFamily="Georgia, serif" fill="#D4AF37" fontWeight="700">
                {h.sign_index}
              </text>
              {/* Planets — stacked below sign number */}
              {(h.planets || []).map((p, i) => (
                <text key={i} x={cx} y={cy + s * 0.015 + i * s * 0.038} fontSize={s * 0.036} fontFamily="Helvetica, sans-serif" fill="#0B1F3A" fontWeight="600" textAnchor="middle">
                  {p}
                </text>
              ))}
              {/* Ascendant tick */}
              {isLagna && (
                <g>
                  <circle cx={cx - s * 0.05} cy={cy - s * 0.02} r={s * 0.024} fill="#D4AF37" opacity="0.15" />
                  <text x={cx - s * 0.05} y={cy - s * 0.012} fontSize={s * 0.024} fill="#D4AF37" fontWeight="700" textAnchor="middle">L</text>
                </g>
              )}
              {/* Highlight ascendant house edge */}
              {h.house === highlight && (
                <circle cx={cx + s * 0.05} cy={cy - s * 0.02} r={s * 0.018} fill="none" stroke="#D4AF37" strokeWidth="1" strokeDasharray="2 2" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
